import { addChildNode, breadcrumb } from "./domain.js";
import { isIotaObjectId } from "./twinscope-api.js";

export const MAX_IMAGE_BYTES = 8 * 1024 * 1024;
export const MAX_DOCUMENT_BYTES = 31 * 1024 * 1024;
export function validateDocumentSize(document) {
  if (new TextEncoder().encode(JSON.stringify(document)).byteLength > MAX_DOCUMENT_BYTES) throw new Error("documentTooLarge");
}
export function restoreBindings(document, bindings, plantId) {
  const contains = (node, id) => node.id === id || (node.children || []).some((child) => contains(child, id));
  const configs = { ...document.configs }, twinIds = { ...document.twinIds };
  let changed = false;
  for (const binding of bindings) {
    if (!contains(document.tree, binding.nodeId) || (binding.plantId && binding.plantId !== plantId)) continue;
    if (!twinIds[binding.nodeId] && isIotaObjectId(binding.twinId)) { twinIds[binding.nodeId] = binding.twinId; changed = true; }
    if (!configs[binding.nodeId] && binding.config) { configs[binding.nodeId] = binding.config; changed = true; }
  }
  return changed ? { ...document, configs, twinIds } : document;
}
export const localId = (kind = "node") => `${kind}-${crypto.randomUUID()}`;
export function newPlantDocument(id, name, coordinates) {
  return { tree: { id, name, coordinates, type: "site", children: [] }, media: {}, configs: {}, twinIds: {}, events: [] };
}
export function resolveMedia(document, nodeId) {
  const owner = breadcrumb(document.tree, nodeId).reverse().find((node) => document.media?.[node.id]?.imageSrc);
  return owner ? { ownerId: owner.id, ...document.media[owner.id] } : null;
}
// Context is read-only and separate from selectable/editable child boxes.
export function enteredAreaContext(document, nodeId, media) {
  if (!media?.imageSrc || nodeId === document.tree.id) return [];
  // Copies of the parent image are still the same graphical coordinate space.
  // Only a different image represents the entire entered node.
  const ancestors = breadcrumb(document.tree, nodeId).slice(0, -1).reverse();
  const parentMedia = ancestors.map(node => document.media?.[node.id]).find(value => value?.imageSrc);
  const own = document.media?.[nodeId];
  if (own?.imageSrc && (!parentMedia || own.imageSrc !== parentMedia.imageSrc ||
      (own.imageId && parentMedia.imageId && own.imageId !== parentMedia.imageId))) return [];
  const sources = [media, ...Object.values(document.media || {})];
  const seen = new Set();
  return sources.flatMap(source => {
    if (source.imageSrc !== media.imageSrc ||
        (source.imageId && media.imageId && source.imageId !== media.imageId)) return [];
    return (source.areas || []).filter(area => {
      if (area.targetId !== nodeId) return false;
      const key = area.id || [area.targetId, area.x, area.y, area.w, area.h].join(":");
      if (seen.has(key)) return false;
      seen.add(key); return true;
    });
  });
}
export function visibleMediaAreas(media, selected, indexed, enteredLevel = false) {
  if (!selected || !indexed) return [];
  const parentId = !enteredLevel && selected.type === "component" && !selected.children?.length
    ? indexed.parents.get(selected.id) || selected.id
    : selected.id;
  return (media?.areas || []).filter((area) => indexed.parents.get(area.targetId) === parentId);
}
export function renameNode(tree, id, name) {
  return { ...tree, ...(tree.id === id ? { name } : {}), ...(tree.children ? { children: tree.children.map((child) => renameNode(child, id, name)) } : {}) };
}
export function saveLocalArea(document, parentId, draft, label, mediaOwnerId) {
  const targetId = draft.targetId || localId();
  const area = { ...draft, id: draft.id || localId("area"), targetId, parentId, label };
  const inherited = resolveMedia(document, parentId);
  const ownerId = mediaOwnerId || inherited?.ownerId || parentId;
  const media = document.media?.[ownerId] || { assetName: "", imageSrc: "", imageId: "", areas: [] };
  const areas = (media.areas || []).filter((item) => item.id !== area.id).concat(area);
  const tree = addChildNode(document.tree, parentId, { id: targetId, name: label, type: draft.nodeType || "component", children: [] });
  return { ...document, tree: renameNode(tree, targetId, label), media: { ...document.media, [ownerId]: { ...media, areas } } };
}
export function readImage(file) {
  if (!file || !/^image\/(png|jpeg|webp|gif)$/.test(file.type)) return Promise.reject(new Error("imageFormat"));
  if (file.size > MAX_IMAGE_BYTES) return Promise.reject(new Error("imageTooLarge"));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("imageReadError"));
    reader.readAsDataURL(file);
  });
}
